import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import {
  ArrowBendUpLeft, PushPin, DotsThreeVertical, Smiley,
  ShareNetwork, Flag,
} from "@phosphor-icons/react";
import AttachmentChip from "./message/AttachmentChip";
import CallCardBubble from "./message/CallCardBubble";
import MessageMenu from "./message/MessageMenu";
import { renderInlineFormat } from "@/lib/renderInlineFormat";

const REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏", "👏", "🔥"];
const PRIORITY_STYLES = {
  low: { color: "bg-gray-200 text-gray-700", label: "LOW" },
  medium: { color: "bg-orange-200 text-orange-800", label: "MED" },
  high: { color: "bg-red-500 text-white", label: "HIGH" },
};

// Backwards-compatible wrapper — old call sites used `renderWithMentions`,
// which only styled @mentions. We now delegate to the shared inline
// renderer (bold, italic, URLs + mentions), which is a strict superset:
// plain-text messages that don't contain `**`, `_`, or `http(s)://` render
// byte-identical to before.
function renderWithMentions(body, mine) {
  return renderInlineFormat(body, mine);
}

export default function MessageBubble({
  msg, mine, isGroup, currentUserId, deleteWindowSec, editWindowSec,
  onChanged, onReply, onShowInfo, onForward, showHeader = true,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [translated, setTranslated] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.body);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen && !emojiOpen) return;
    const onDoc = (e) => { if (!menuRef.current?.contains(e.target)) { setMenuOpen(false); setEmojiOpen(false); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen, emojiOpen]);

  // ---- Capability flags ----
  const ageSec = (Date.now() - new Date(msg.created_at).getTime()) / 1000;
  const canEdit = mine && ageSec < (editWindowSec || 300) && !msg.deleted_for_all;
  const canDeleteForAll = mine && ageSec < (deleteWindowSec || 300) && !msg.deleted_for_all;
  const readByOthers = isGroup
    ? (msg.read_by || []).some(u => u !== currentUserId)
    : !!msg.read_at;
  const canRecall = mine && !readByOthers && !msg.deleted_for_all;
  const starredByMe = (msg.starred_by || []).includes(currentUserId);

  // ---- Avatar palette ----
  const initials = (msg.from_user_name || "?").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const avatarHue = ([...((msg.from_user_name || "?"))].reduce((a, c) => a + c.charCodeAt(0), 0)) % 360;
  const avatarBg = mine ? "hsl(245, 50%, 92%)" : `hsl(${avatarHue}, 55%, 88%)`;
  const avatarFg = mine ? "#5B5FC7" : `hsl(${avatarHue}, 55%, 30%)`;

  // ---- Action wiring ----
  const callApi = async (fn) => {
    setBusy(true);
    try { await fn(); onChanged?.(); }
    catch (e) { console.error(e); }
    finally { setBusy(false); setMenuOpen(false); setEmojiOpen(false); }
  };
  const reactTo = (emoji) => callApi(() => api.post(`/team-chat/messages/${msg.id}/react`, { emoji }));
  const togglePin = () => callApi(() => api.post(`/team-chat/messages/${msg.id}/pin`));
  const toggleStar = () => callApi(() => api.post(`/team-chat/messages/${msg.id}/star`));
  const delForMe = () => callApi(() => api.delete(`/team-chat/messages/${msg.id}?scope=me`));
  const delForAll = () => { if (confirm("Delete for everyone?")) callApi(() => api.delete(`/team-chat/messages/${msg.id}?scope=all`)); };
  const recallMsg = () => { if (confirm("Recall this message? (Allowed because no one has read it yet.)")) callApi(() => api.delete(`/team-chat/messages/${msg.id}?scope=recall`)); };
  const copyMsg = () => { navigator.clipboard.writeText(msg.body); setMenuOpen(false); };
  const translateMsg = async () => {
    setMenuOpen(false);
    const target = window.prompt("Translate to (e.g. es, fr, de, hi, ja):", "es");
    if (!target) return;
    try {
      const { data } = await api.post(`/team-chat/messages/${msg.id}/translate`, { target });
      setTranslated({ text: data.translated, target });
    } catch (e) { alert("Translation failed: " + (e.response?.data?.detail || e.message)); }
  };
  const submitEdit = async () => {
    if (!draft.trim() || draft === msg.body) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/team-chat/messages/${msg.id}`, { body: draft.trim() });
      setEditing(false);
      onChanged?.();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  // iter135 P1: replace bare tick glyphs with human-readable status text.
  // Keeps the existing HelloByFlow typography (10px muted, right-aligned).
  const statusText = () => {
    if (msg.deleted_for_all || !mine) return null;
    if (msg._optimistic || msg.status === "sending" || msg.status === "queued") {
      return <span className="text-[10px] text-[var(--text-muted)]" data-testid={`msg-status-${msg.id}`}>Sending</span>;
    }
    if (msg.status === "failed" || msg.status === "undelivered") {
      return <span className="text-[10px] text-red-500 font-semibold" data-testid={`msg-status-${msg.id}`}>Failed</span>;
    }
    const seen = isGroup
      ? (msg.read_by || []).filter(u => u !== currentUserId).length > 0
      : !!msg.read_at;
    if (seen) return <span className="text-[10px] text-[#5B5FC7] font-semibold" data-testid={`msg-status-${msg.id}`}>Seen</span>;
    if (msg.delivered_at) return <span className="text-[10px] text-[var(--text-muted)]" data-testid={`msg-status-${msg.id}`}>Delivered</span>;
    return <span className="text-[10px] text-[var(--text-muted)]" data-testid={`msg-status-${msg.id}`}>Sent</span>;
  };

  // ---- Special-case render paths ----
  if (msg.deleted_for_all) {
    return (
      <div className="flex gap-2 px-1" data-testid={`team-msg-${msg.id}`}>
        <div className="w-9 shrink-0" />
        <div className="text-[12px] italic text-[var(--text-muted)] py-1">
          {msg.deleted_scope === "recall" ? "Message recalled" : "This message was deleted"}
        </div>
      </div>
    );
  }
  if (msg.call_room_id) {
    return <CallCardBubble msg={msg} mine={mine} showHeader={showHeader} avatarBg={avatarBg} avatarFg={avatarFg} />;
  }

  const reactionEntries = Object.entries(msg.reactions || {}).filter(([_, users]) => users.length > 0);

  // ---- Regular message bubble ----
  return (
    <div className={`group px-3 ${showHeader ? "mt-4" : "mt-1"}`} data-testid={`team-msg-${msg.id}`}>
      {/* Sender name sits ABOVE the avatar+bubble row, indented past the avatar
          column so it aligns with the bubble's left edge (matches Teams-style
          reference: `Daniel Franklin` above the DF avatar + speech bubble). */}
      {showHeader && (
        <div className="ml-9 mb-1 flex items-baseline gap-2" data-testid={`team-msg-sender-${msg.id}`}>
          <span className="font-semibold text-[13px] text-[var(--text-primary)]">{msg.from_user_name || "Someone"}</span>
          {msg.edited_at && <span className="text-[10px] italic text-[var(--text-muted)]">edited</span>}
        </div>
      )}
      <div className="flex gap-2">
        <div className="w-7 shrink-0 pt-0.5">
          {showHeader && (
            <div className="w-7 h-7 rounded-full flex items-center justify-center font-semibold text-[10px]"
                 style={{ background: avatarBg, color: avatarFg }}>
              {initials}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 relative">
          {msg.pinned && (
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#5B5FC7] font-semibold mb-1">
              <PushPin size={10} weight="fill" /> Pinned
            </div>
          )}

        <div className="max-w-[85%]">
          <div className="flex items-end gap-2">
          <div className={`relative px-4 py-2.5 text-[15px] leading-relaxed rounded-2xl ${
            mine ? "bg-[#EBEBFC] text-[var(--text-primary)]" : "bg-[#F0F0F4] text-[var(--text-primary)]"
          }`}>
            {msg.reply_preview && (
              <div className="mb-1.5 px-2 py-1 border-l-2 border-[#5B5FC7] bg-white/60 text-[11px]">
                <div className="font-semibold text-[#5B5FC7]">{msg.reply_preview.from_user_name}</div>
                <div className="truncate text-[var(--text-secondary)]">{msg.reply_preview.body}</div>
              </div>
            )}

            {msg.forwarded && (
              <div className="mb-1 text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1 text-[var(--text-muted)]"
                   data-testid={`msg-forwarded-${msg.id}`}>
                <ShareNetwork size={10} /> Forwarded{msg.forwarded_from?.from_user_name ? ` from ${msg.forwarded_from.from_user_name}` : ""}
              </div>
            )}

            {msg.priority && PRIORITY_STYLES[msg.priority] && (
              <div className={`inline-flex items-center gap-1 mb-1 px-1.5 py-0.5 text-[10px] font-bold rounded-sm ${PRIORITY_STYLES[msg.priority].color}`}
                   data-testid={`msg-priority-${msg.id}`}>
                <Flag size={10} weight="fill" /> {PRIORITY_STYLES[msg.priority].label}
              </div>
            )}

            {(msg.attachments || []).length > 0 && (
              <div className="space-y-1.5 my-1" data-testid={`msg-attachments-${msg.id}`}>
                {msg.attachments.map((a, i) => <AttachmentChip key={`${msg.id}-att-${i}-${a.filename || a.url || ''}`} att={a} msg={msg} mine={false} />)}
              </div>
            )}

            {editing ? (
              <div>
                <textarea value={draft} onChange={e => setDraft(e.target.value)} autoFocus rows={2}
                          className="w-full bg-white text-[var(--text-primary)] px-2 py-1 text-[14px] border border-[var(--border)] rounded"
                          data-testid={`msg-edit-input-${msg.id}`} />
                <div className="flex gap-1 mt-1">
                  <button onClick={submitEdit} disabled={busy} className="text-[11px] bg-[#5B5FC7] text-white px-2 py-0.5 font-semibold disabled:opacity-50 rounded" data-testid={`msg-edit-save-${msg.id}`}>Save</button>
                  <button onClick={() => { setEditing(false); setDraft(msg.body); }} className="text-[11px] bg-white px-2 py-0.5 border border-[var(--border)] rounded">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="whitespace-pre-wrap break-words">
                {renderWithMentions(msg.body, false)}
              </div>
            )}

            {translated && (
              <div className="mt-1.5 pt-1.5 border-t border-[var(--border)] text-[13px] italic text-[var(--text-secondary)]"
                   data-testid={`msg-translated-${msg.id}`}>
                <span className="text-[10px] uppercase tracking-widest font-semibold text-[#5B5FC7] mr-1">{translated.target}:</span>
                {translated.text}
                <button onClick={() => setTranslated(null)} className="ml-2 text-[10px] underline opacity-70 hover:opacity-100">hide</button>
              </div>
            )}

            {/* Hover action bar */}
            <div className="absolute -top-3.5 right-2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-[var(--border)] shadow-sm rounded">
              <button onClick={() => setEmojiOpen(v => !v)} className="p-1 hover:bg-[var(--bg-sidebar)]" title="React" data-testid={`msg-react-btn-${msg.id}`}>
                <Smiley size={14} />
              </button>
              <button onClick={() => onReply(msg)} className="p-1 hover:bg-[var(--bg-sidebar)]" title="Reply" data-testid={`msg-reply-btn-${msg.id}`}>
                <ArrowBendUpLeft size={14} />
              </button>
              <button onClick={() => setMenuOpen(v => !v)} className="p-1 hover:bg-[var(--bg-sidebar)]" title="More" data-testid={`msg-menu-btn-${msg.id}`}>
                <DotsThreeVertical size={14} weight="bold" />
              </button>
            </div>
          </div>
          {/* Timestamp + status live OUTSIDE the bubble, aligned to its
              right edge and vertically anchored to the bubble's bottom
              (`items-end` on the flex row above). Matches the Teams-
              style reference: "Yes bhai   04:43". */}
          <span className="text-[10px] text-[var(--text-muted)] shrink-0 pb-1 whitespace-nowrap flex items-center gap-1"
                data-testid={`team-msg-meta-${msg.id}`}>
            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {starredByMe && <span className="text-yellow-500">★</span>}
            {statusText()}
          </span>
          </div>

          {/* Reactions chips */}
          {reactionEntries.length > 0 && (
            <div className="flex gap-1 mt-0.5 flex-wrap" data-testid={`msg-reactions-${msg.id}`}>
              {reactionEntries.map(([emoji, users]) => {
                const reactedByMe = users.includes(currentUserId);
                return (
                  <button key={emoji} onClick={() => reactTo(emoji)}
                          className={`text-[12px] px-2 py-0.5 rounded-full border ${reactedByMe ? "bg-[#EBEBFC] border-[#5B5FC7] text-[#5B5FC7]" : "bg-white border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-sidebar)]"}`}
                          data-testid={`msg-reaction-${msg.id}-${emoji}`}>
                    {emoji} {users.length}
                  </button>
                );
              })}
              <button onClick={() => setEmojiOpen(true)} className="text-[11px] w-6 h-6 rounded-full border border-[var(--border)] bg-white hover:bg-[var(--bg-sidebar)] flex items-center justify-center" title="Add reaction"
                      data-testid={`msg-add-reaction-${msg.id}`}>
                <Smiley size={12} />
              </button>
            </div>
          )}

          {/* Emoji picker */}
          {emojiOpen && (
            <div ref={menuRef} className="absolute z-30 mt-1 top-full left-0 bg-white border border-[var(--border)] shadow-lg p-1.5 flex gap-1 rounded"
                 data-testid={`msg-emoji-picker-${msg.id}`}>
              {REACTIONS.map(e => (
                <button key={e} onClick={() => reactTo(e)} className="text-[18px] hover:bg-[var(--bg-sidebar)] w-7 h-7 flex items-center justify-center rounded"
                        data-testid={`msg-emoji-${msg.id}-${e}`}>{e}</button>
              ))}
            </div>
          )}

          {/* Context menu */}
          {menuOpen && (
            <MessageMenu
              msg={msg}
              menuRef={menuRef}
              starredByMe={starredByMe}
              canEdit={canEdit}
              canRecall={canRecall}
              canDeleteForAll={canDeleteForAll}
              onReply={() => { onReply(msg); setMenuOpen(false); }}
              onForward={() => { onForward?.(msg); setMenuOpen(false); }}
              onTranslate={translateMsg}
              onCopy={copyMsg}
              onStartEdit={() => { setEditing(true); setMenuOpen(false); }}
              onToggleStar={toggleStar}
              onTogglePin={togglePin}
              onShowInfo={() => { onShowInfo(msg); setMenuOpen(false); }}
              onDelForMe={delForMe}
              onRecall={recallMsg}
              onDelForAll={delForAll}
            />
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
